import { useCallback } from 'react';
import uniq from 'lodash/uniq';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { LOAD_SUBTASKS_EXPERIENCE } from '../../common/constants';
import { useBoardStore, useGetIssueById } from '../../services/board-store';
import { useGetSubtasks } from '../../services/get-subtasks-jis';
import LocalStorage from '../../utils/local-storage-old';

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useCardSubtasks = () => {
	const [{ subtaskParentMap }, { updateIssue }] = useBoardStore();

	const [getIssueById] = useGetIssueById();
	const { getSubtasks } = useGetSubtasks();
	const projectData = useProject();
	const projectId = String(projectData.id);

	const startExperience = useExperienceStart(LOAD_SUBTASKS_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(LOAD_SUBTASKS_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(LOAD_SUBTASKS_EXPERIENCE);

	const isExpanded = useCallback(
		(issueId: number) => LocalStorage.getExpandedTasks(projectId).indexOf(issueId) !== -1,
		[projectId],
	);

	const loadSubtasks = useCallback(
		async (parentIssueId: number) => {
			startExperience();

			// optimistic update to show loading skeleton if we have no current children
			const hasChildren = getIssueById(parentIssueId)?.subtasks != null;
			if (!hasChildren) {
				updateIssue(parentIssueId, { subtasks: [] });
			}

			const { data: oldSubtasks, error } = await getSubtasks({
				parentIssueIds: [parentIssueId],
			});

			if (oldSubtasks != null) {
				updateIssue(parentIssueId, { subtasks: oldSubtasks });
				LocalStorage.setExpandedTasks(
					projectId,
					uniq([...LocalStorage.getExpandedTasks(projectId), parentIssueId]),
				);
				markExperienceSuccess();
			} else if (error != null) {
				// revert optimistic update
				updateIssue(parentIssueId, { subtasks: undefined });
				markExperienceFailed(error.message, error);
			}
		},
		[
			startExperience,
			getIssueById,
			getSubtasks,
			updateIssue,
			projectId,
			markExperienceSuccess,
			markExperienceFailed,
		],
	);

	const hideSubtasks = useCallback(
		(parentIssueId: number) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			updateIssue(parentIssueId, { subtasks: undefined } as {
				subtasks: undefined;
			});
			LocalStorage.setExpandedTasks(
				projectId,
				LocalStorage.getExpandedTasks(projectId).filter((issueId) => parentIssueId !== issueId),
			);
		},
		[projectId, updateIssue],
	);

	const toggleSubtasks = useCallback(
		(parentIssueId: number) => {
			if (isExpanded(parentIssueId)) {
				hideSubtasks(parentIssueId);
			} else {
				loadSubtasks(parentIssueId);
			}
		},
		[hideSubtasks, loadSubtasks, isExpanded],
	);

	const onIssueUpdated = useCallback(
		(issueId: number) => {
			const storedIssue = getIssueById(issueId);
			// Only top-level cards are stored in the board store, subtasks are not
			if (storedIssue) {
				if (isExpanded(issueId)) {
					loadSubtasks(issueId);
				}
			} else {
				// If issue is a subtask we actually want the parent task to refetch subtask details
				getSubtasks({ issueIds: [issueId] }).then((response) => {
					const subtaskIssue = response.data?.[0];
					if (subtaskIssue && subtaskIssue.parentId != null && isExpanded(subtaskIssue.parentId)) {
						loadSubtasks(subtaskIssue.parentId);
					}
				});
			}
		},
		[getIssueById, loadSubtasks, getSubtasks, isExpanded],
	);

	const onIssueDeleted = useCallback(
		(issueId: number) => {
			const parentIssueId = subtaskParentMap[issueId];
			if (parentIssueId != null) {
				const remainingSubtasks = getIssueById(subtaskParentMap[issueId])?.subtasks?.filter(
					(subtask) => subtask.id !== issueId,
				);
				updateIssue(parentIssueId, {
					// empty array indicates subtasks are loading, need to clear value when no subtasks left
					subtasks:
						Array.isArray(remainingSubtasks) && remainingSubtasks.length > 0
							? remainingSubtasks
							: undefined,
				});
			}
		},
		[subtaskParentMap, updateIssue, getIssueById],
	);

	return {
		isExpanded,
		loadSubtasks,
		hideSubtasks,
		toggleSubtasks,
		onIssueUpdated,
		onIssueDeleted,
	};
};
